(() => {
  if (window.BPDailyEvent) {
    window.BPDailyEvent.initialize(document);
    return;
  }

  const instances = new WeakMap();
  const timestamp = /^\d{4}-(0[1-9]|1[0-2])-([0-2]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:Z|[+-](?:0\d|1[0-4]):[0-5]\d)$/;
  const parseTimestamp = (value) => {
    if (!timestamp.test(value || '')) return null;
    const date = Date.parse(value);
    if (!Number.isFinite(date)) return null;
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    const day = Number(value.slice(8, 10));
    const hour = Number(value.slice(11, 13));
    const minute = Number(value.slice(14, 16));
    const second = Number(value.slice(17, 19));
    const offset = value.endsWith('Z') ? 0 :
      (value[19] === '+' ? 1 : -1) * (Number(value.slice(20, 22)) * 60 + Number(value.slice(23, 25)));
    const maxDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day < 1 || day > maxDay || Math.abs(offset) > 840 ||
        (Math.abs(offset) === 840 && Number(value.slice(23, 25)) !== 0)) return null;
    return Date.UTC(year, month - 1, day, hour, minute, second) - offset * 60000 === date ? date : null;
  };

  class DailyEvent {
    constructor(root) {
      this.root = root;
      this.gateway = root.previousElementSibling?.matches('[data-bp-brand-gateway]') ? root.previousElementSibling :
        document.querySelector('[data-bp-brand-gateway]');
      this.card = this.gateway?.querySelector('[data-bp-event-card]');
      this.nav = this.gateway?.querySelector('[data-bp-event-nav]');
      this.start = parseTimestamp(root.dataset.eventStarts);
      this.end = parseTimestamp(root.dataset.eventEnds);
      this.abort = new AbortController();
      this.timeout = null;
      this.interval = null;
      this.active = false;
    }

    initialize() {
      const now = Date.now();
      if (this.start === null || this.end === null || this.end <= this.start ||
          now < this.start || now >= this.end || !this.card || !this.nav) {
        this.removeEvent();
        return;
      }
      this.active = true;
      this.root.dataset.bpEventValid = 'true';
      this.card.hidden = false;
      this.nav.hidden = false;
      this.nav.dataset.bpGatewayJump = String(Array.from(this.gateway.querySelectorAll('[data-bp-gateway-panel]')).indexOf(this.card));
      this.gateway.dataset.bpEventActive = 'true';
      const heading = this.gateway.querySelector('[data-bp-event-worlds-heading]');
      const navLabel = this.gateway.querySelector('[data-bp-monogram-nav]');
      if (heading) { heading.dataset.bpEventOriginal = heading.textContent; heading.textContent = heading.dataset.eventLabel; }
      if (navLabel) { navLabel.dataset.bpEventOriginal = navLabel.getAttribute('aria-label') || ''; navLabel.setAttribute('aria-label', navLabel.dataset.eventLabel); }
      this.bindVariantForm();
      this.bindRuntime();
      this.gateway?.querySelector('[data-bp-monogram="event"]')?.classList.add('bp-event-monogram');
      window.BPBrandGateway?.refresh?.(this.gateway);
      window.BPUniverse?.synchronize?.({ source: 'event-available', transition: false });
    }

    bindRuntime() {
      const { signal } = this.abort;
      const update = () => {
        const remaining = this.end - Date.now();
        if (remaining <= 0) { this.expire(); return; }
        const days = Math.floor(remaining / 86400000);
        const hours = Math.floor((remaining % 86400000) / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        const countdown = this.card?.querySelector('[data-bp-event-countdown]');
        const values = { days, hours, minutes, seconds };
        Object.entries(values).forEach(([unit, value]) => {
          const cell = countdown?.querySelector(`[data-bp-event-${unit}]`);
          if (cell) cell.textContent = String(value).padStart(2, '0');
        });
        const output = countdown?.querySelector('[data-bp-event-time]');
        if (output) output.textContent = `${days}g ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
        const monogram = this.gateway?.querySelector('[data-bp-monogram="event"]');
        monogram?.classList.toggle('is-breathing', !document.hidden &&
          !this.gateway.classList.contains('is-editor-paused'));
      };
      const scheduleExpiry = () => {
        if (this.timeout) clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          if (Date.now() >= this.end) this.expire();
          else scheduleExpiry();
        }, Math.min(this.end - Date.now(), 2147483647));
      };
      update();
      scheduleExpiry();
      this.interval = setInterval(update, 1000);
      document.addEventListener('visibilitychange', update, { signal });
      window.addEventListener('bp:universe-state-change', update, { signal });
    }

    bindVariantForm() {
      const form = this.root.querySelector('form');
      const idInput = form?.querySelector('[data-bp-event-variant-id]');
      const submit = form?.querySelector('[data-bp-event-submit]');
      const price = this.root.querySelector('[data-bp-event-price]');
      const availability = this.root.querySelector('[data-bp-event-availability]');
      if (!form || !idInput || !submit) return;
      let variants = [];
      try { variants = JSON.parse(this.root.querySelector('[data-bp-event-variants]')?.textContent || '[]'); } catch (_) {}
      const options = Array.from(form.querySelectorAll('[data-bp-event-option]'));
      const select = form.querySelector('[data-bp-event-variant]');
      const formatPrice = (cents) => {
        const currency = window.Shopify?.currency?.active || 'EUR';
        try { return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', { style: 'currency', currency }).format(Number(cents) / 100); }
        catch (_) { return price?.textContent || ''; }
      };
      const update = () => {
        const variant = options.length ? variants.find((item) =>
          item.options?.every((value, index) => value === options[index]?.value)) :
          variants.find((item) => String(item.id) === select?.value);
        const available = Boolean(variant?.available);
        idInput.value = available ? String(variant.id) : '';
        submit.disabled = !available;
        if (price && variant) price.textContent = formatPrice(variant.price);
        if (availability) availability.textContent = available ? '' : this.root.dataset.eventUnavailable || '';
      };
      [...options, select].filter(Boolean).forEach((control) => control.addEventListener('change', update, { signal: this.abort.signal }));
      update();
    }

    expire() {
      if (!this.active) return;
      const selected = window.BPUniverse?.getState().universe === 'event';
      const focused = this.card?.contains(document.activeElement) || this.nav?.contains(document.activeElement) || this.root.contains(document.activeElement);
      this.root.dataset.bpEventValid = 'false';
      if (selected) window.BPUniverse?.setState('mystery', { source: 'event-expired', persist: true, announce: true, transition: false });
      if (focused) this.gateway?.querySelector('[data-universe="mystery"] [data-bp-gateway-trigger]')?.focus();
      this.removeEvent();
      window.BPUniverse?.synchronize?.({ source: 'event-expired', transition: false });
    }

    removeEvent() {
      this.active = false;
      if (this.timeout) clearTimeout(this.timeout);
      if (this.interval) clearInterval(this.interval);
      this.abort.abort();
      this.nav?.remove();
      this.card?.remove();
      this.gateway?.removeAttribute('data-bp-event-active');
      const heading = this.gateway?.querySelector('[data-bp-event-worlds-heading]');
      const navLabel = this.gateway?.querySelector('[data-bp-monogram-nav]');
      if (heading?.dataset.bpEventOriginal) heading.textContent = heading.dataset.bpEventOriginal;
      if (navLabel?.dataset.bpEventOriginal) navLabel.setAttribute('aria-label', navLabel.dataset.bpEventOriginal);
      this.root.remove();
      if (this.gateway) window.BPBrandGateway?.refresh?.(this.gateway);
      instances.delete(this.root);
    }

    destroy() {
      if (this.timeout) clearTimeout(this.timeout);
      if (this.interval) clearInterval(this.interval);
      this.abort.abort();
      instances.delete(this.root);
    }
  }

  const rootsWithin = (container) => {
    const roots = [];
    if (container instanceof Element && container.matches('[data-bp-daily-event]')) roots.push(container);
    container.querySelectorAll?.('[data-bp-daily-event]').forEach((root) => roots.push(root));
    return roots;
  };
  const initialize = (container = document) => rootsWithin(container).forEach((root) => {
    if (instances.has(root)) return;
    const instance = new DailyEvent(root);
    instances.set(root, instance);
    instance.initialize();
  });
  const destroy = (container) => rootsWithin(container).forEach((root) => instances.get(root)?.destroy());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  window.BPDailyEvent = Object.freeze({ initialize, destroy });
})();
